import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { scrollToFirstErrorField } from '../utils/scrollToFirstError';
import { ValidationError } from '../admin/components/ValidationError';
import { submitReport } from '../services/api.shop';
import { ReportDto } from '../types';
import { useTranslation } from '../context/TranslationContext';

interface ReportSellerModalProps {
    sellerId: string;
    sellerName: string;
    isOpen: boolean;
    onClose: () => void;
}

const ReportSellerModal: React.FC<ReportSellerModalProps> = ({
    sellerId,
    sellerName,
    isOpen,
    onClose,
}) => {
    const { t } = useTranslation();
    const [reason, setReason] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const reasons = [
        'Inappropriate image or content',
        "Product doesn't match description",
        'Poor communication',
        'Suspicious / scam behavior',
        'Other',
    ];

    useEffect(() => {
        if (!isOpen) return;
        setReason('');
        setDescription('');
        setError(null);
        setLoading(false);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        document.body.classList.add('scroll-locked');
        return () => document.body.classList.remove('scroll-locked');
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        if (!reason) {
            setError('Please select a reason for the report.');
            scrollToFirstErrorField();
            return;
        }

        setLoading(true);

        try {
            const dto: ReportDto = {
                reason,
                description: description || undefined,
                sellerId: sellerId,
            };

            await submitReport(dto);

            alert('Thank you! Our team will review this report shortly \u{1F6A9}');
            onClose();
        } catch (err: unknown) {
            let errorMessage = 'Failed to submit report. Please try again.';

            if (err && typeof err === 'object' && err !== null) {
                const response = (err as { response?: unknown }).response;

                if (response && typeof response === 'object' && response !== null) {
                    const data = (response as { data?: unknown }).data;

                    if (typeof data === 'string') {
                        if (data.includes('cannot report yourself')) {
                            errorMessage = 'You cannot report yourself.';
                        } else {
                            errorMessage = data;
                        }
                    } else if (data && typeof data === 'object') {
                        const message = (data as { message?: string }).message;
                        if (message) errorMessage = message;
                    }
                }
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const fieldBorder =
        'border-[color-mix(in_srgb,var(--accent-color),transparent_50%)]';
    const mutedText = 'text-[color-mix(in_srgb,var(--text-color),transparent_35%)]';

    return createPortal(
        <div
            className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/60 p-4"
            role="presentation"
            onClick={onClose}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="report-seller-title"
                className="max-h-[min(90dvh,40rem)] w-full max-w-md overflow-y-auto overflow-x-hidden rounded-3xl border border-[color-mix(in_srgb,var(--accent-color),transparent_40%)] bg-[var(--bg-color)] shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-6 pb-2 pt-6">
                    <h2
                        id="report-seller-title"
                        className="text-2xl font-semibold text-[var(--text-color)]"
                    >
                        {t('component.reportseller.title')}
                    </h2>
                    <p className={`mt-1 ${mutedText}`}>{t('component.reportseller.subtitle')}</p>
                </div>

                <form onSubmit={handleSubmit} className="px-6 pb-6">
                    <div className="mb-4">
                        <p className={`mb-2 text-sm ${mutedText}`}>
                            Reporting:{' '}
                            <span className="font-medium text-[var(--text-color)]">{sellerName}</span>
                        </p>
                    </div>

                    <div className="mb-6 space-y-2">
                        {reasons.map((r) => (
                            <label
                                key={r}
                                className={`flex cursor-pointer items-center gap-3 rounded-2xl border ${fieldBorder} px-4 py-3 transition hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_85%)]`}
                            >
                                <input
                                    type="radio"
                                    name="reason"
                                    value={r}
                                    checked={reason === r}
                                    onChange={(e) => {
                                        setReason(e.target.value);
                                        setError(null);
                                    }}
                                    className="accent-[var(--accent-color)]"
                                />
                                <span className="text-[var(--text-color)]">{r}</span>
                            </label>
                        ))}
                    </div>

                    <ValidationError error={error ?? undefined} />

                    <textarea
                        placeholder="Please give us more details (optional)"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={3}
                        className={`w-full rounded-2xl border ${fieldBorder} bg-[color-mix(in_srgb,var(--bg-color),white_30%)] p-4 text-base text-[var(--text-color)] focus:border-[var(--accent-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)]/30`}
                    />

                    <div className="mt-6 flex gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className={`min-h-11 flex-1 rounded-2xl border ${fieldBorder} py-3 font-medium text-[var(--text-color)] hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_85%)]`}
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={loading || !reason}
                            className="min-h-11 flex-1 rounded-2xl bg-[var(--accent-color)] py-3 font-medium text-white disabled:opacity-50"
                        >
                            {loading ? t('state.sending') : t('component.reportseller.submit')}
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
};

export default ReportSellerModal;
